<template>
  <div class="style-select-popover-root">
    <el-popover
      trigger="click"
      :placement="placement"
      :teleported="true"
      :width="292"
      popper-class="style-select-popover"
    >
      <template #reference>
        <slot />
      </template>
      <div class="style-select-popover__body">
        <ScrollBar v-if="styleItems.length" class="style-select-popover__scroll" :item-gap="10">
          <div
            v-for="item in styleItems"
            :key="item.thumb"
            class="style-select-popover__item"
            :class="{ 'style-select-popover__item--active': modelValue === item.thumb }"
            :title="item.thumb"
            @click="onSelect(item)"
          >
            <img class="style-select-popover__img" :src="item.thumb" alt="" />
          </div>
        </ScrollBar>
        <div v-else class="style-select-popover__empty">加载中...</div>
      </div>
    </el-popover>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted } from 'vue';
  import { ElPopover } from 'element-plus';
  import type { Placement } from 'element-plus';
  import ScrollBar from '@/components/common/list/ScrollBar.vue';
  import type { TextStyleData, TextStyleJsonData } from '@/types/pixiText-type';
  import { convertJsonToData } from '@/utils/pixiTextStyleUtil';

  export interface StyleItem {
    thumb: string;
    style: TextStyleJsonData;
  }

  export interface StylePreset {
    thumb: string;
    style: TextStyleData;
  }

  defineProps<{
    placement?: Placement;
  }>();

  const modelValue = defineModel<string>({ default: '' });

  const emit = defineEmits<{
    select: [preset: StylePreset];
  }>();

  const BASE = import.meta.env.BASE_URL;
  const styleItems = ref<StyleItem[]>([]);

  onMounted(async () => {
    try {
      const res = await fetch(`${BASE}textStyle/data.json`);
      const data: StyleItem[] = await res.json();
      // 为缩略图路径补上 base，确保 dev/test 环境都能正确加载
      styleItems.value = data.map((item) => ({
        ...item,
        thumb: `${BASE}${item.thumb}`
      }));
    } catch (e) {
      console.error('Failed to load text style data:', e);
    }
  });

  function onSelect(item: StyleItem) {
    modelValue.value = item.thumb;
    const style = convertJsonToData(item.style);
    emit('select', { thumb: item.thumb, style });
  }
</script>

<style>
  .style-select-popover {
    padding: 13px !important;
    border-radius: 8px !important;
    border: 1px solid #eff0f7 !important;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.14) !important;
  }
</style>

<style scoped>
  .style-select-popover-root {
    display: contents;
  }

  .style-select-popover__body {
    width: 100%;
    overflow-x: hidden;
  }

  .style-select-popover__scroll :deep(.list-container) {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
  }

  .style-select-popover__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 13px;
    color: #9ca3af;
  }

  .style-select-popover__item {
    aspect-ratio: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f8fafc;
    border-radius: 10px;
    cursor: pointer;
    border: 2px solid transparent;
    box-sizing: border-box;
    transition:
      border-color 0.2s,
      box-shadow 0.2s,
      background-color 0.2s;
    flex-shrink: 0;
    overflow: hidden;
  }

  .style-select-popover__item:hover {
    background-color: #f1f5f9;
  }

  .style-select-popover__item--active {
    border-color: #6033e1;
    box-shadow: 0 0 3px rgba(96, 51, 225, 0.12);
    background-color: #fbf9ff;
  }

  .style-select-popover__img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    pointer-events: none;
  }
</style>
